import { AfterViewChecked, AfterViewInit, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Temporal } from '@js-temporal/polyfill';
import { Observable, tap } from 'rxjs';
import { AssessmentStatusEnum } from 'src/app/enums/assessment-status.enum';
import { GoalPeriodStatus } from 'src/app/enums/goal-period-status.enum';
import { AssessmentHelper } from 'src/app/helpers/assessment.helper';
import { ReviewPeriodHelper } from 'src/app/helpers/review-period.helper';
import { EmployeeForAssessments } from 'src/app/models/employee-for-assessments.model';
import { Employee } from 'src/app/models/employee.model';
import { ReviewFilterOptions } from 'src/app/models/other/review-filter-options.model';
import { ReviewPeriod } from 'src/app/models/review-period.model';
import { State } from 'src/app/models/state.model';
import { AssessmentService, GoalService, StateService } from 'src/app/services';
import { ReviewTypeStrategyBase } from 'src/app/services/products/review-type-strategy-base';
import { ReviewTypeStrategyFactory } from 'src/app/services/products/review-type-strategy.factory';
import { DateHelper } from 'src/app/shared/helpers';
import { EmployeeHelper } from 'src/app/helpers/employee.helper';

@Component({
  selector: 'app-assessment-list-item-details',
  templateUrl: './assessment-list-item-details.component.html',
  styleUrl: './assessment-list-item-details.component.scss'
})
export class AssessmentListItemDetailsComponent implements OnInit {

  @Input() public reviewPeriod: ReviewPeriod;
  @Input() public employee: Employee;
  @Input() public closedGroup = false;

  @Output() public onLoading = new EventEmitter<void>();
  @Output() public onLoaded = new EventEmitter<void>();

  public state: State;
  public details$: Observable<EmployeeForAssessments[]>;
  public displayedColumns = ['name', 'email', 'deadline', 'status', 'action'];
  public EmployeeHelper = EmployeeHelper;

    isGaugeMedium = false;
    isDialogOpened = false;

    originalFormData: any;

  public reviewTypeStrategy: ReviewTypeStrategyBase;

  public get isReviewPeriodOpen(): boolean {
    return ReviewPeriodHelper.isRpOpen(this.reviewPeriod);
  }

  public get isPanoramaAuraGoals(): boolean {
    return ReviewPeriodHelper.isPanoramaAuraGoals(this.reviewPeriod.reviewType);
  }

  public goalGaugeValue(item: EmployeeForAssessments): number {
    return item.goalPeriodResult != null ? item.goalPeriodResult * 100 : 0; 
  }

  public panoramaGaugeValue(item: EmployeeForAssessments): number {
    return item.panoramaResult != null ? item.panoramaResult * 100 : 0; 
  }

  public isFinished = AssessmentHelper.isFinished;
  public isSziluett = ReviewPeriodHelper.isSziluett;
  public isDateExpired = DateHelper.isDateExpired;


  constructor(
    private assessmentService: AssessmentService,
    private stateService: StateService,
    private goalService: GoalService,
    private router: Router,
    private translate: TranslateService
  ) {
    this.state = stateService.state;
  }

  ngOnInit(): void {
    this.onLoading.emit();
    this.reviewTypeStrategy = ReviewTypeStrategyFactory.create(this.reviewPeriod?.reviewType);
    this.loadDetails();
  }

  getDeadline(item: EmployeeForAssessments): Temporal.PlainDate {
    if (ReviewPeriodHelper.isSziluett(this.reviewPeriod.reviewType) && item.deadline) return item.deadline;
    else if (this.isPanoramaAuraGoals && this.reviewPeriod.panoramaAuraEndDate) return this.reviewPeriod.panoramaAuraEndDate;
    else 
          return null;
  } 

  showDeadline(item: EmployeeForAssessments): boolean {
    return this.getDeadline(item) != null && !this.isFinished(item.status);
  }

  loadDetails() {
    let options = {
      reviewPeriodId: this.reviewPeriod.id,
      onlyClosedRpe: this.closedGroup === true,
      onlyNotClosedRpe: this.closedGroup === false,
    } as ReviewFilterOptions;
    this.details$ = this.assessmentService.getEmployeesToReview(options).pipe(
      tap(() => {
       this.onLoaded.emit();
      })
    );
  }

  onReviewClick(item: EmployeeForAssessments) {
    if (this.isFinished(item.status)) return;

    this.router.navigate(['/assessment', item.reviewPeriodEmployeeId]);
  }

  onGoalsClick(item: EmployeeForAssessments) {
    this.router.navigate(['/goals', this.reviewPeriod.id, item.id ]);
  }

  onReportClick(item: EmployeeForAssessments) {
    this.router.navigate(['/reports/panorama-aura', item.id, this.reviewPeriod.id]);
  }

  isPanoramaReportAllowed(element: EmployeeForAssessments): boolean {
    if (!this.isPanoramaAuraGoals || !this.isReportReady(element)) return false;

    if (this.state.isSuperAdmin || this.state.isAdmin || this.state.isAssistant) return true;

    // a saját riportját láthatja, ha engedélyezték neki ezt külön.
    if (element.isSelf && this.employee.isOwnPanoramaAuraReportAllowed) return true;

    // Minden más esetben: ha a bejelentkezett felhasználó a vezetője az elemben szereplő személynek.
    return element.isSubordinate;
  }

  // Akkor érhető el a riport gomb Panoráma esetén ha mindenki értékelte már személyt, vagy az időszak már lezárt 
  public isReportReady(element: EmployeeForAssessments): boolean {
    return element.isAllReviewClosed || ReviewPeriodHelper.isRpClosed(this.reviewPeriod) || this.isDateExpired(this.reviewPeriod.panoramaAuraEndDate);
  }

  public isReviewAllowed(element: EmployeeForAssessments): boolean {
    if (!this.reviewPeriod || !this.isReviewPeriodOpen || this.isFinished(element?.status)) 
      return false;

    return this.reviewTypeStrategy.isReviewAllowed(this.reviewPeriod, element.isSelf);
  }

  // goals

  public isShowGoalAllowed(element: EmployeeForAssessments): boolean {
    return this.reviewPeriod && this.reviewPeriod.isGoalsEnabled && element.isGoalEvaluationTarget && (element.isSelf || element.isSubordinate);
  }

  public isGoalRedirectAllowed(element: EmployeeForAssessments): boolean {
    return (element.isSelf || element.isSubordinate) && this.reviewPeriod.isGoalsEnabled;
  }

  public isShowGoalIndicatorAllowed(element: EmployeeForAssessments): boolean {
    if (!this.reviewPeriod || !element.goalPeriodStatus) return false;

    return element.goalPeriodStatus === GoalPeriodStatus.Finished || element.goalPeriodStatus === GoalPeriodStatus.Approved;
  }

  public allowGoalsListPage(element:EmployeeForAssessments): boolean {
    if (element.goalPeriodStatus === GoalPeriodStatus.LeaderAssessment && this.state.me.value.employee.id === element.id)
      return false;

    return true;
  }

  //--

  public getStatusText(element: EmployeeForAssessments): string {
    //element.status == 0 && (closedGroup || !element.isSelf)

    if (element.status != AssessmentStatusEnum.New) {
      switch (element.status) {
        case AssessmentStatusEnum.InProgress:
          return this.translate.instant('ASSESSMENT_STATUS_TEXT.INCOMPLETE');
        case AssessmentStatusEnum.Finished:
          return this.translate.instant('ASSESSMENT_STATUS_TEXT.COMPLETED');
        default:
          return '';
      }
    }
    else
    { // New
      if (this.closedGroup) {
        return this.translate.instant('ASSESSMENT_STATUS_TEXT.NOT_FILLED');
      }
      else {
        if (this.isSziluett(this.reviewPeriod.reviewType)) {
          if (element.isSelf)
            return '';
          else
            return this.translate.instant('ASSESSMENT_STATUS_TEXT.NOT_FILLED');
        }
        else
          return this.translate.instant('ASSESSMENT_STATUS_TEXT.NOT_FILLED');
      }
    }

    return '';
  }

  public getGoalStatusText(element: EmployeeForAssessments): string {
    //element.status == 0 && (closedGroup || !element.isSelf)
    return this.goalService.getGoalStatusText(element.goalPeriodStatus);
  }

}
